import {
  Autocomplete,
  FormControl,
  FormLabel,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField
} from '@mui/material'
import { isEmpty } from 'lodash'
import { useState } from 'react'
import styled from 'styled-components'

import { USER_ATTRIBUTES } from '@/components/User'
import useNotificator from '@/pages/SpaceNotificatorPage/hooks/useNotificator'
import { Card, FormData } from '@/pages/SpaceNotificatorPage/types'
import { useAppSelector } from '@/store/hooks'
import { getCurrentSpace, getCurrentUser } from '@/store/selectors'
import fetch from '@/utilities/fetch'

import Preview from './Preview'
import Selectors from './Selectors'

const HelpButton = styled.button`
  border: none;
  align-self: end;
  padding: 0;
  cursor: pointer;
  margin: 0;
  text-align: center;
  text-decoration: none;
  color: ${({ theme }) => theme.notificator.wgColorModerate};
`

const FormSection = styled(TableCell)`
  border-bottom: none !important;
  padding: 10px !important;
`

const FooterPanel = styled.div`
  display: block;
  width: 100%;
  padding: 1rem;
  border-top: 1px solid rgba(224, 224, 224, 1);
  text-align: center;
`

const SummaryPanel = styled.div`
  display: flex;
  flex-direction: column;
  padding: 1rem;
  border-top: 0.25rem solid ${({ theme }) => theme.notificator.wgColorLow};
`

const PanelHeading = styled.h1`
  font-size: ${({ theme }) => theme.notificator.wgTextBigger};
  font-weight: bold;
  text-align: center;
  margin-top: 0;
`

const ErrorMessage = styled.span`
  color: ${({ theme }) => theme.notificator.wgColorHigh};
  font-size: ${({ theme }) => theme.notificator.wgTextSmall};
  font-weight: bold;
  margin-bottom: 1rem;
`

const ImportantMessage = styled.span`
  color: ${({ theme }) => theme.notificator.wgColorDark};
  font-size: ${({ theme }) => theme.notificator.wgTextSmall};
  font-weight: bold;
  margin-bottom: 1rem;
`

const StatusMessage = styled.span`
  color: ${({ theme }) => theme.notificator.wgColorNew};
  font-size: ${({ theme }) => theme.notificator.wgTextSmall};
  font-weight: normal;
  margin-bottom: 1rem;
  margin-left: 1rem;
`

const SuccessMessage = styled.span`
  color: ${({ theme }) => theme.notificator.wgColorNew};
  font-size: ${({ theme }) => theme.notificator.wgTextSmall};
  font-weight: bold;
  margin-bottom: 1rem;
`

const PrimaryButton = styled.button`
  border-radius: 0.5rem;
  padding: 0.75rem;

  width: 100%;
  cursor: pointer;
  display: block;
  font-weight: bold;
  margin: 0;
  text-align: center;
  text-decoration: none;
  text-transform: uppercase;
  background-color: ${({ theme }) => theme.notificator.wgColorPrimary};
  border: 1px solid ${({ theme }) => theme.notificator.wgColorPrimary};
  color: ${({ theme }) => theme.notificator.wgColorWhite};
`

const DestructiveButton = styled.button`
  border-radius: 0.5rem;
  padding: 0.75rem;

  width: auto;
  cursor: pointer;
  display: block;
  font-weight: bold;
  margin: 2rem 0;
  text-align: center;
  text-decoration: none;
  text-transform: uppercase;
  font-size: 0.5rem;
  background-color: ${({ theme }) => theme.notificator.wgColorHigh};
  border: 1px solid ${({ theme }) => theme.notificator.wgColorHigh};
  color: ${({ theme }) => theme.notificator.wgColorWhite};
`

function Summary({ onHelp }: { onHelp: (mode: 'general' | 'selectors') => void }) {
  const currentUser = useAppSelector(getCurrentUser)
  const currentSpace = useAppSelector(getCurrentSpace)
  const notificator = useNotificator()

  const [audienceOptions, setAudienceOptions] = useState([currentUser])

  // Form status
  const STATUSES = {
    DELETE_ERROR: 'delete_error',
    DELETE_SUCCESS: 'delete_success',
    SUBMIT_ERROR: 'error',
    PROCESSING: 'processing',
    SUBMIT_SUCCESS: 'success',
    PENDING: 'pending',
    VALIDATION: 'validation',
    SEARCHING: 'searching'
  }
  const [status, setStatus] = useState(STATUSES.PENDING)

  // Default form data
  const [formData, setFormData] = useState({
    audience: currentUser?.userName || currentUser?.email,
    approver: 'Jane Smith',
    submitter: 'James Durgin',
    sourceName: '',
    sourceIcon: '',
    expire: 14
  } as FormData)

  const [selectedCards, setSelectedCards] = useState<Array<Card>>()

  // Update the form data (non-dropdown fields) when a change is made
  const handleChange = (event: { target: { name: string | number; value: any } }) => {
    const { name, value } = event.target

    // Set in state
    setFormData(prevFormData => ({
      ...prevFormData,
      [name]: value
    }))

    setStatus(STATUSES.PENDING)
  }

  // Events from dropdown selection are built differently
  const handleAudienceChange = (event: any, value: any) => {
    handleChange({ target: { name: 'audience', value: value?.userName || '' } })
  }

  // Look up valid audience users based on input
  const lookupAudience = async (event: { target: any }, value: any) => {
    // If we're currently waiting on a lookup, don't search again - we'll likely skip some typing, but we're ok with it
    if (status !== STATUSES.SEARCHING && value?.length >= 3) {
      // Reset the status
      setStatus(STATUSES.SEARCHING)
      const response = await fetch(`/organizations/:organizationId/spaces/:spaceId/users/search/basic`, {
        method: 'POST',
        body: {
          attributes: USER_ATTRIBUTES,
          count: 10,
          startIndex: 1,
          spaceId: currentSpace.id,
          filter: USER_ATTRIBUTES.map(attribute => `${attribute} co ${JSON.stringify(value)}`).join(' or ')
        }
      })
      setStatus(STATUSES.PENDING)
      // This may fail on spaces with a large amount of members, but for the purposes of this app in small demo spaces,
      // and usually sending to the logged in user anyway, we're not too worried about it.  If the lookup fails, just
      // make sure the logged in user is always available
      if (response?.resources.length > 0) {
        setAudienceOptions(response.resources)
      } else {
        setAudienceOptions([currentUser])
      }
    } else {
      setAudienceOptions([currentUser])
    }
  }

  const updateSelection = (updateData: Array<Card>) => {
    setSelectedCards(updateData)

    // Reset status
    setStatus(STATUSES.PENDING)
  }

  const updateSource = (source: object) => {
    setFormData(prevFormData => ({
      ...prevFormData,
      ...source
    }))
  }

  //Required fields are audience, submitter, approver and selected cards
  const validateForm = () => {
    return (
      !isEmpty(formData.audience) &&
      !isEmpty(formData.submitter) &&
      !isEmpty(formData.approver) &&
      formData.expire > 0 &&
      formData.expire <= 365 &&
      !isEmpty(selectedCards)
    )
  }

  // Submit request to send card(s)
  const handleSubmit = async (event: { preventDefault: () => void }) => {
    event.preventDefault()
    if (!validateForm()) {
      setStatus(STATUSES.VALIDATION)
      return
    }
    setStatus(STATUSES.PROCESSING)

    const result = await notificator.submit.mutateAsync({
      ...formData,
      selectedCards
    })

    result.status === 'success' ? setStatus(STATUSES.SUBMIT_SUCCESS) : setStatus(STATUSES.SUBMIT_ERROR)
  }

  // Confirm the user wants to clear their toolbar
  const confirmClear = async () => {
    if (
      window.confirm(
        'Delete all notifications from toolbar?  This will only remove notifications sent via the emulator and only for the logged in user (not the user specified in the Send field).'
      )
    ) {
      const result = await notificator.clear.mutateAsync({
        user: currentUser?.userName || currentUser?.email
      })

      result.status === 'success' ? setStatus(STATUSES.DELETE_SUCCESS) : setStatus(STATUSES.DELETE_ERROR)
    }
  }

  return (
    <SummaryPanel>
      <PanelHeading>Send Demo Messages</PanelHeading>
      <form onSubmit={handleSubmit}>
        <Table>
          <TableBody>
            <TableRow>
              <FormSection>
                <FormControl variant="standard" fullWidth>
                  <FormLabel>
                    Send Cards To
                    <StatusMessage>
                      {status === STATUSES.SEARCHING ? 'Searching for users...' : '(3+ characters)'}
                    </StatusMessage>
                  </FormLabel>
                  <Autocomplete
                    id="audienceLookup"
                    defaultValue={currentUser}
                    onChange={handleAudienceChange}
                    clearOnBlur={true}
                    clearOnEscape={true}
                    isOptionEqualToValue={(
                      option: { displayName: string; userName: string },
                      value: { displayName: string; userName: string }
                    ) => {
                      return option.userName === value.userName
                    }}
                    noOptionsText="No matching users found in space"
                    onInputChange={lookupAudience}
                    renderOption={(props, option: { displayName: string; userName: string }) => (
                      <MenuItem {...props} key={option.userName} value={option.userName}>
                        {option.displayName + ' - ' + option.userName}
                      </MenuItem>
                    )}
                    options={audienceOptions.map((option: { displayName: string; userName: string }) => {
                      return option
                    })}
                    getOptionLabel={(option: any) => option.userName}
                    renderInput={params => (
                      <TextField
                        variant="standard"
                        value={formData.audience}
                        name={'audience'}
                        aria-label="Send Cards To"
                        {...params}
                        placeholder={currentUser?.userName || currentUser?.email}
                        InputProps={{
                          ...params.InputProps,
                          type: 'text'
                        }}
                      />
                    )}
                  />
                </FormControl>
              </FormSection>
              <FormSection>
                <HelpButton name="help" type="button" onClick={() => onHelp('general')}>
                  ?
                </HelpButton>
              </FormSection>
            </TableRow>
            <TableRow>
              <FormSection>
                <FormControl variant="standard" fullWidth>
                  <FormLabel>Use Submitter Name</FormLabel>
                  <TextField
                    variant="standard"
                    aria-label="Use Submitter Name"
                    name="submitter"
                    value={formData.submitter}
                    onChange={handleChange}
                  />
                </FormControl>
              </FormSection>
            </TableRow>
            <TableRow>
              <FormSection>
                <FormControl variant="standard" fullWidth>
                  <FormLabel>Use Approver Name</FormLabel>
                  <TextField
                    variant="standard"
                    aria-label="Use Approver Name"
                    name="approver"
                    value={formData.approver}
                    onChange={handleChange}
                  />
                </FormControl>
              </FormSection>
            </TableRow>
            <TableRow>
              <FormSection>
                <FormControl variant="standard" fullWidth>
                  <FormLabel>Days before card expires (1 - 365)</FormLabel>
                  <TextField
                    variant="standard"
                    aria-label="Days before card expires (1 - 365)"
                    name="expire"
                    value={formData.expire}
                    onChange={handleChange}
                    type={'number'}
                  />
                </FormControl>
              </FormSection>
            </TableRow>
            <Selectors
              fullWidth
              setSelected={updateSelection}
              setSource={updateSource}
              onHelp={() => onHelp('selectors')}
            />
          </TableBody>
        </Table>
        <FooterPanel>
          <ErrorMessage style={status === STATUSES.SUBMIT_ERROR ? { display: 'block' } : { display: 'none' }}>
            Unable to send card(s)
          </ErrorMessage>
          <ErrorMessage style={status === STATUSES.DELETE_ERROR ? { display: 'block' } : { display: 'none' }}>
            Unable to delete cards
          </ErrorMessage>
          <ErrorMessage style={status === STATUSES.VALIDATION ? { display: 'block' } : { display: 'none' }}>
            Please complete all fields
          </ErrorMessage>
          <ImportantMessage style={status === STATUSES.PROCESSING ? { display: 'block' } : { display: 'none' }}>
            Your request is processing...
          </ImportantMessage>
          <SuccessMessage style={status === STATUSES.SUBMIT_SUCCESS ? { display: 'block' } : { display: 'none' }}>
            Your request was sent successfully. It may take a few minutes for notifications to appear.
          </SuccessMessage>
          <SuccessMessage style={status === STATUSES.DELETE_SUCCESS ? { display: 'block' } : { display: 'none' }}>
            Your request was sent successfully. It may take a few minutes for notifications and badges to disappear, or
            you can refresh.
          </SuccessMessage>
          <PrimaryButton name="submit" type="submit">
            Submit
          </PrimaryButton>
          <DestructiveButton name="clearToolbar" type="button" onClick={() => confirmClear()}>
            Clear My Toolbar
          </DestructiveButton>
        </FooterPanel>
        {selectedCards ? <Preview selectedCard={selectedCards} /> : ''}
      </form>
    </SummaryPanel>
  )
}

export default Summary
